import { Link, useParams } from "react-router";
import {
  Archive01Icon,
  Calendar01Icon,
  MailIcon,
  NotesEdit01Icon,
  Users02Icon,
} from "vega-icons";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatShortSlashDate } from "@vega-core/utils/formatDate";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { CloseDealCard } from "../components/CloseDealCard/CloseDealCard";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { useDealName } from "../hooks/useDealName";

const configureDealInformationCardClass =
  "rounded-lg border border-border/primary bg-background/primary p-5 hover:bg-background/component focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default";

export const ConfigurePage = () => {
  const { id: dealId = "" } = useParams<{ id: string }>();

  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const { featureFlag } = useFeatureFlag();
  const isCloseDealEnabled =
    featureFlag[FeatureFlag.COINVEST_REPORTING_VD_26152];
  const isOutreachEnabled =
    featureFlag[FeatureFlag.COINVEST_OUTREACH_EMAIL_VD_27885];

  const { data: dealResponse } = useGetDealGeneralPartnerViewV3(dealId, {
    query: {
      enabled: !!dealId,
      select: (data) => data.data,
    },
  });

  const closingDate = dealResponse?.deal.closingDate;
  const dealStatus = dealResponse?.deal.status;
  const isDealClosed = dealStatus === DealStatusEnum.CLOSED;
  const showCloseDealCard =
    isCloseDealEnabled && dealStatus !== DealStatusEnum.CANCELLED;

  return (
    <div className="my-4 max-w-3xl mx-auto">
      <CoInvestmentPageHeader
        fundName={dealName}
        isLoading={isDealNameLoading}
        title={m.coInvestments_nav_configure()}
      />

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Link
          className={cnTwMerge(
            configureDealInformationCardClass,
            "col-span-full",
          )}
          to={CoInvestmentFullPaths.CoInvestmentDetails.replace(":id", dealId)}
        >
          <div className="flex justify-between items-start">
            <Users02Icon className="text-icon/secondary" />
            {closingDate ? (
              <div
                className="flex items-center gap-1.5 body-small-regular text-text/secondary"
                data-testid="configure-closing-date"
              >
                <Calendar01Icon />
                {formatShortSlashDate(closingDate)}
              </div>
            ) : null}
          </div>
          <h3 className="heading-5 mt-3">
            {m.coInvestments_nav_dealInformation()}
          </h3>
          <p className="body-base-regular text-text/secondary mt-1">
            {m.coInvestments_configure_hub_dealInformation_subtitle()}
          </p>
        </Link>

        <Link
          className={configureDealInformationCardClass}
          to={CoInvestmentFullPaths.CoInvestmentDataRoom.replace(":id", dealId)}
        >
          <Archive01Icon className="text-icon/secondary" />
          <h3 className="heading-5 mt-3">{m.coInvestments_nav_dataRoom()}</h3>
          <p className="body-base-regular text-text/secondary mt-1">
            {m.coInvestments_configure_hub_dataRoom_subtitle()}
          </p>
        </Link>

        <Link
          className={configureDealInformationCardClass}
          to={CoInvestmentFullPaths.CoInvestmentNdaManagement.replace(
            ":id",
            dealId,
          )}
        >
          <div className="flex justify-between items-start">
            <NotesEdit01Icon className="text-icon/secondary" />
          </div>
          <h3 className="heading-5 mt-3">
            {m.coInvestments_nav_ndaManagement()}
          </h3>
          <p className="body-base-regular text-text/secondary mt-1">
            {m.coInvestments_configure_hub_ndaManagement_subtitle()}
          </p>
        </Link>

        {isOutreachEnabled ? (
          <Link
            className={cnTwMerge(
              configureDealInformationCardClass,
              "col-span-full",
            )}
            data-testid="configure-email-template-card"
            to={CoInvestmentFullPaths.CoInvestmentEmailTemplates.replace(
              ":id",
              dealId,
            )}
          >
            <MailIcon className="text-icon/secondary" />
            <h3 className="heading-5 mt-3">
              {m.coInvestments_nav_emailTemplate()}
            </h3>
            <p className="body-base-regular text-text/secondary mt-1">
              {m.coInvestments_configure_hub_emailTemplate_subtitle()}
            </p>
          </Link>
        ) : null}
      </div>

      {showCloseDealCard ? (
        <CloseDealCard
          closedAt={dealResponse?.deal.closedAt}
          closingDate={closingDate}
          dealId={dealId}
          isClosed={isDealClosed}
        />
      ) : null}
    </div>
  );
};
